/**
 * MultipleSelectAnswerButton Component - Checkbox-based answer button for multiple-select questions
 * Allows users to select multiple options before submitting
 */

import React, { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import textToSpeech from '../utils/textToSpeech';
import { HAPTIC_TYPES } from '../constants/audio';

const MultipleSelectAnswerButton = memo(({ 
  option, 
  index, 
  currentQuestion,
  answerLocked,
  correctAnswers,
  question,
  questions,
  onAnswer,
  triggerHaptic,
  selectedAnswers,
  onToggle
}) => {
  const buttonColors = [
    'from-blue-400 via-blue-500 to-blue-600',
    'from-pink-400 via-pink-500 to-pink-600',
    'from-green-400 via-green-500 to-green-600',
    'from-purple-400 via-purple-500 to-purple-600'
  ];
  const textColors = [
    'text-blue-50',
    'text-pink-50',
    'text-green-50',
    'text-purple-50'
  ];
  const colorIndex = index % 4;
  
  const isSelected = Array.isArray(selectedAnswers) && selectedAnswers.includes(option);
  const isCorrect = Array.isArray(correctAnswers) && correctAnswers.includes(option);
  const showResult = answerLocked;

  const handleToggle = useCallback(() => {
    if (answerLocked) return;
    triggerHaptic(HAPTIC_TYPES.LIGHT);
    onToggle(option);
  }, [answerLocked, option, onToggle, triggerHaptic]);

  const handleKeyDown = useCallback((e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !answerLocked) {
      e.preventDefault();
      triggerHaptic(HAPTIC_TYPES.LIGHT);
      onToggle(option);
    }
  }, [answerLocked, option, onToggle, triggerHaptic]);

  const handleSpeak = useCallback(() => {
    textToSpeech.toggleSpeak(option, { rate: 0.92, pitch: 1.05 });
  }, [option]);

  // Determine button styling based on state
  let buttonClass = `answer-option flex-1 min-w-0 p-5 md:p-6 text-2xl md:text-3xl font-bold rounded-2xl shadow-xl transition-all duration-200 border-4 min-h-[80px] md:min-h-[100px] flex items-center justify-center whitespace-nowrap bg-gradient-to-br ${buttonColors[colorIndex]} ${textColors[colorIndex]}`;
  
  if (showResult) {
    if (isCorrect) {
      buttonClass = `answer-option flex-1 min-w-0 p-5 md:p-6 text-2xl md:text-3xl font-bold rounded-2xl shadow-xl transition-all duration-200 border-4 border-green-500 min-h-[80px] md:min-h-[100px] flex items-center justify-center whitespace-nowrap bg-gradient-to-br from-green-400 via-green-500 to-green-600 text-white`;
    } else if (isSelected && !isCorrect) {
      buttonClass = `answer-option flex-1 min-w-0 p-5 md:p-6 text-2xl md:text-3xl font-bold rounded-2xl shadow-xl transition-all duration-200 border-4 border-red-500 min-h-[80px] md:min-h-[100px] flex items-center justify-center whitespace-nowrap bg-gradient-to-br from-red-400 via-red-500 to-red-600 text-white`;
    } else {
      buttonClass = `answer-option flex-1 min-w-0 p-5 md:p-6 text-2xl md:text-3xl font-bold rounded-2xl shadow-xl transition-all duration-200 border-4 border-gray-300 min-h-[80px] md:min-h-[100px] flex items-center justify-center whitespace-nowrap bg-gray-200 text-gray-600 opacity-60`;
    }
  } else if (isSelected) {
    buttonClass += ' ring-4 ring-yellow-400 ring-offset-2 scale-105';
  }

  return (
    <div className="flex items-center gap-2 md:gap-3 min-w-0">
      <div 
        onClick={handleToggle}
        className={`${buttonClass} ${!answerLocked ? 'hover:scale-110 active:scale-95 cursor-pointer' : 'cursor-not-allowed'}`}
        style={{
          boxShadow: showResult 
            ? (isCorrect ? '0 8px 16px rgba(34, 197, 94, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)' 
                : isSelected && !isCorrect ? '0 8px 16px rgba(239, 68, 68, 0.4), inset 0 1px 0 rgba(255,255,255,0.3)'
                : '0 4px 8px rgba(0,0,0,0.1)')
            : '0 8px 16px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
          touchAction: 'manipulation'
        }}
        role="checkbox"
        aria-checked={isSelected}
        aria-label={`${isSelected ? 'Selected' : 'Unselected'} answer option: ${option}`}
        tabIndex={0}
        onKeyDown={handleKeyDown}
      >
        <div className="flex items-center gap-3">
          <span className={`text-3xl ${isSelected ? 'opacity-100' : 'opacity-50'}`}>
            {isSelected ? '☑' : '☐'}
          </span>
          <span className="text-center px-2" style={{ textShadow: '1px 1px 2px rgba(0,0,0,0.2)' }}>{option}</span>
        </div>
      </div>
      <button
        onClick={handleSpeak}
        className="bg-purple-500 hover:bg-purple-600 text-white p-3 md:p-4 rounded-full transition-all shadow-lg flex-shrink-0 min-w-[48px] min-h-[48px] md:min-w-[56px] md:min-h-[56px] text-xl md:text-2xl flex items-center justify-center border-2 border-white"
        style={{ touchAction: 'manipulation' }}
        title="Listen to answer"
        aria-label={`Listen to answer: ${option}`}
      >
        🔊
      </button>
    </div>
  );
});

MultipleSelectAnswerButton.propTypes = {
  option: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  currentQuestion: PropTypes.number.isRequired,
  answerLocked: PropTypes.bool.isRequired,
  correctAnswers: PropTypes.array.isRequired,
  question: PropTypes.object.isRequired,
  questions: PropTypes.array.isRequired,
  onAnswer: PropTypes.func.isRequired,
  triggerHaptic: PropTypes.func.isRequired,
  selectedAnswers: PropTypes.array.isRequired,
  onToggle: PropTypes.func.isRequired
};

MultipleSelectAnswerButton.displayName = 'MultipleSelectAnswerButton';

export default MultipleSelectAnswerButton;

